'use client'

import { EitherOr } from '@qr-front/ui/utils'
import { colorSchemeOptionListAtom } from 'features/generator/contentFormSections/client/ContentDesignForm'
import {
	contactInfoEmailListAtom,
	contactInfoPhoneListAtom,
	contactInfoWebsiteAtom,
	normalizeEmailEntries,
	normalizePhoneEntries,
	serializeContactEntries,
	TContactField,
} from 'features/generator/contentFormSections/CommonSections/ContactSection'
import {
	addressCityAtom,
	addressCountryAtom,
	addressPostalCodeAtom,
	addressStateAtom,
	addressStreetAtom,
	addressUrlAtom,
} from 'features/generator/contentFormSections/LocationForm/addressAtoms'
import { generateCustomPlatformId } from 'features/generator/contentFormSections/SocialLinksForm/CustomSocialLinkFields'
import { normalizeSocialLinkLogo } from 'features/generator/contentFormSections/SocialLinksForm/normalizeSocialLinkLogo'
import { summaryAtom } from 'features/generator/contentFormSections/SummaryForm/SummarySection'
import { atom } from 'jotai'

import {
	primaryColorAtom,
	secondaryColorAtom,
	selectedColorSchemeAtom,
} from '@/content-from-sections/client'
import { TUploadedMedia } from '@/services'
import { getFileFromMedia, isOnServer } from '@/utils'

import {
	aboutAvatarImageAtom,
	aboutAvatarImageFileAtom,
	aboutCompanyNameAtom,
	aboutFullNameAtom,
	aboutProfessionAtom,
	socialLinkListAtom,
	TQrColorScheme,
	TSocialLinkField,
	welcomeImageAtom,
	welcomeImageFileAtom,
} from '../../../contentFormSections'
import { QR_TYPE } from '../../../generatorTypes'
import { SOCIAL_PLATFORM_LIST } from '../../../utils/socialPlatformsConfig'

export type TVCardContent = {
	type: QR_TYPE.VCard
	colors: Omit<TQrColorScheme, 'name'>
	profile: {
		name: string
		surname: string
		image: TUploadedMedia
	}
	contacts: {
		phoneNumber: TContactField
		alternativePhoneNumber: string
		website: string
		email: TContactField
	}
	company: {
		name: string
		position: string
	}
	summary: string
	address: EitherOr<
		{ url: string },
		{
			country: string
			state: string
			city: string
			street: string
			postalCode: string
		}
	>
	links: TSocialLinkField[]
	loaderImage: TUploadedMedia
}

export const vCardContentAtom = atom<TVCardContent, [TVCardContent], void>(
	(get) => ({
		type: QR_TYPE.VCard,
		colors: {
			primary: get(primaryColorAtom),
			secondary: get(secondaryColorAtom),
		},
		profile: {
			name: get(aboutFullNameAtom),
			surname: '',
			image:
				get(aboutAvatarImageAtom) ||
				({
					publicId: '',
					resourceType: '',
					format: '',
					bytes: 0,
				} as TUploadedMedia),
		},
		contacts: {
			phoneNumber: serializeContactEntries(get(contactInfoPhoneListAtom)),
			alternativePhoneNumber: '',
			website: get(contactInfoWebsiteAtom) || '',
			email: serializeContactEntries(get(contactInfoEmailListAtom)),
		},
		company: {
			name: get(aboutCompanyNameAtom) || '',
			position: get(aboutProfessionAtom) || '',
		},
		summary: get(summaryAtom) || '',
		address: get(addressUrlAtom)
			? { url: get(addressUrlAtom) || '' }
			: {
					street: get(addressStreetAtom) || '',
					postalCode: get(addressPostalCodeAtom) || '',
					city: get(addressCityAtom) || '',
					state: get(addressStateAtom) || '',
					country: get(addressCountryAtom) || '',
				},
		links: get(socialLinkListAtom).map(
			({ name, url, description = '', logo }) => {
				return {
					name,
					url,
					description,
					logo: normalizeSocialLinkLogo(logo),
				}
			},
		),
		loaderImage:
			get(welcomeImageAtom) ||
			({
				publicId: '',
				resourceType: '',
				format: '',
				bytes: 0,
			} as TUploadedMedia),
	}),
	(get, set, content) => {
		// Color scheme
		const colorScheme = get(colorSchemeOptionListAtom).find(
			(scheme) =>
				scheme.primary === content.colors.primary &&
				scheme.secondary === content.colors.secondary,
		)

		set(selectedColorSchemeAtom, colorScheme?.name ?? '')
		set(primaryColorAtom, content.colors.primary)
		set(secondaryColorAtom, content.colors.secondary)

		// Personal
		const fullName = [content.profile.name, content.profile.surname]
			.filter(Boolean)
			.join(' ')
		set(aboutFullNameAtom, fullName)
		set(aboutAvatarImageAtom, content.profile.image)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const avatarFile = await getFileFromMedia(
						content.profile.image,
					)
					set(aboutAvatarImageFileAtom, avatarFile)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}

		// Contact
		set(
			contactInfoPhoneListAtom,
			normalizePhoneEntries(
				content.contacts.phoneNumber,
				content.contacts.alternativePhoneNumber,
			),
		)
		set(contactInfoWebsiteAtom, content.contacts.website)
		set(
			contactInfoEmailListAtom,
			normalizeEmailEntries(content.contacts.email),
		)
		// Company
		set(aboutCompanyNameAtom, content.company.name)
		set(aboutProfessionAtom, content.company.position)
		// Summary
		set(summaryAtom, content.summary)
		// Address
		if (content.address?.url) {
			set(addressUrlAtom, content.address.url)
		} else {
			set(addressStreetAtom, content.address.street)
			set(addressPostalCodeAtom, content.address.postalCode)
			set(addressCityAtom, content.address.city)
			set(addressStateAtom, content.address.state)
			set(addressCountryAtom, content.address.country)
		}
		// Social links
		const match = (name: string) =>
			SOCIAL_PLATFORM_LIST.find(
				(platform) =>
					platform.name.toLowerCase() === name.toLowerCase(),
			)
		set(
			socialLinkListAtom,
			content.links.map(({ name, url, description, logo }) => ({
				platformId: match(name)?.id ?? generateCustomPlatformId(),
				name,
				url,
				description,
				logo,
			})),
		)

		// Welcome screen
		set(welcomeImageAtom, content.loaderImage)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const loaderFile = await getFileFromMedia(
						content.loaderImage,
					)
					set(welcomeImageFileAtom, loaderFile)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
	},
)
